

import { useState, useEffect, useRef } from "react";
import "./terms.css";

const sections = [
  {
    id: "acceptance",
    title: "Acceptance of Terms",
    icon: "✦",
    content: [
      {
        text: "By accessing or using TalentHive's platform, you confirm that you are at least 18 years of age, have the legal capacity to enter into binding agreements, and agree to be bound by these Terms and our Privacy Policy.",
      },
      {
        text: "TalentHive reserves the right to modify these Terms at any time. Continued use of the platform after any changes constitutes your acceptance of the new Terms. We will notify registered users of significant changes via email or platform notifications.",
      },
    ],
  },
  {
    id: "platform-use",
    title: "Platform Use & Eligibility",
    icon: "◈",
    content: [
      {
        subtitle: "For Job Seekers",
        text: "You may create one personal account, upload your resume and professional information, apply to job listings, and communicate with prospective employers. You must provide accurate, truthful information and keep your profile up to date.",
      },
      {
        subtitle: "For Employers & Recruiters",
        text: "Organizations may post job listings, search candidate profiles, and contact potential hires through our platform. Job listings must be for genuine, legal employment opportunities. Misleading, fraudulent, or discriminatory postings are strictly prohibited.",
      },
      {
        subtitle: "Prohibited Activities",
        text: "Users may not scrape or harvest data, post spam, distribute malware, impersonate others, or engage in any activity that disrupts platform operations. Violations will result in immediate account termination.",
      },
    ],
  },
  {
    id: "accounts",
    title: "Account Responsibilities",
    icon: "⬡",
    content: [
      {
        text: "You are solely responsible for maintaining the confidentiality of your account credentials. Any activity under your account is your responsibility. Notify TalentHive immediately at security@talenthive.com if you suspect unauthorized access.",
      },
      {
        text: "TalentHive reserves the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or remain inactive for extended periods, without prior notice.",
      },
    ],
  },
  {
    id: "content",
    title: "User Content & IP",
    icon: "◇",
    content: [
      {
        text: "You retain ownership of all content you submit to TalentHive, including resumes, job postings, and messages. By submitting content, you grant TalentHive a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content solely for operating the platform.",
      },
      {
        text: "You warrant that your content does not infringe upon any third-party intellectual property rights, does not contain defamatory or unlawful material, and complies with all applicable laws and regulations.",
      },
    ],
  },
  {
    id: "privacy",
    title: "Privacy & Data Protection",
    icon: "⬢",
    content: [
      {
        subtitle: "Data We Collect",
        text: "We collect profile information, usage data, communication records, and technical identifiers to operate and improve our services. We do not sell your personal data to third parties.",
      },
      {
        subtitle: "How We Use Your Data",
        text: "Your data is used to match candidates with opportunities, improve our algorithms, send relevant notifications, and comply with legal obligations. You can manage your data preferences in account settings.",
      },
      {
        subtitle: "Data Retention",
        text: "We retain your data for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time, subject to legal retention requirements.",
      },
      {
        subtitle: "Cookies",
        text: "TalentHive uses cookies and similar technologies to enhance user experience, analyze platform performance, and deliver relevant content. You may control cookie preferences through your browser settings.",
      },
    ],
  },
  {
    id: "payments",
    title: "Payments & Subscriptions",
    icon: "◉",
    content: [
      {
        text: "Certain features of TalentHive require a paid subscription. All fees are stated in Indian Rupees (INR) and are exclusive of applicable taxes. Payments are processed securely through our third-party payment providers.",
      },
      {
        text: "Subscriptions auto-renew unless cancelled before the renewal date. Refunds are provided only in cases of billing errors or technical failures on our end. Users cancelling mid-cycle will retain access until the end of the paid period.",
      },
    ],
  },
  {
    id: "disclaimers",
    title: "Disclaimers & Liability",
    icon: "△",
    content: [
      {
        text: 'TalentHive is a platform connecting job seekers and employers. We do not guarantee employment outcomes, interview invitations, or the accuracy of third-party job listings. The platform is provided "as is" without warranties of any kind.',
      },
      {
        text: "To the maximum extent permitted by law, TalentHive shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the platform, including but not limited to loss of employment opportunity, revenue, or data.",
      },
    ],
  },
  {
    id: "termination",
    title: "Termination",
    icon: "✕",
    content: [
      {
        text: "You may terminate your account at any time through account settings. TalentHive may terminate or suspend your access immediately, without notice or liability, for any reason including breach of these Terms.",
      },
      {
        text: "Upon termination, your right to use the platform ceases immediately. Provisions that by their nature should survive termination — including IP, disclaimers, and limitation of liability — shall remain in effect.",
      },
    ],
  },
  {
    id: "governing-law",
    title: "Governing Law",
    icon: "⊕",
    content: [
      {
        text: "These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts located in New Delhi, India. You waive any objection to proceedings in such courts.",
      },
      {
        text: "If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect. These Terms constitute the entire agreement between you and TalentHive.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    icon: "◎",
    content: [
      {
        text: "For questions about these Terms, please contact our legal team at talent@talenthive.com. For privacy-related queries, reach our Data Protection Officer at privacy@talenthive.com.",
      },
      {
        text: "TalentHive Technologies Pvt. Ltd.\n91 Springboard, Sector 44\nGurugram, Haryana 122003\nIndia",
      },
    ],
  },
];


export default function TermsAndPolicy() {
  const [activeSection, setActiveSection] = useState("acceptance");
  const [scrolled, setScrolled] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);

      let current = sections[0].id;
      for (const sec of sections) {
        const el = sectionRefs.current[sec.id];
        if (el && el.getBoundingClientRect().top <= 160) {
          current = sec.id;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial call
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      const offset = 140;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="thc-root">
      {/* Body */}
      <div className="thc-body">
        {/* Sidebar */}
        <aside className="thc-sidebar" data-aos="fade-right">
          <div className="thc-sidebar-inner">
            <p className="thc-sidebar-label">On this page</p>
            <nav className="thc-sidebar-nav">
              {sections.map((sec) => (
                <button
                  key={sec.id}
                  className={`thc-sidebar-link ${activeSection === sec.id ? "thc-sidebar-link--active" : ""}`}
                  onClick={() => scrollTo(sec.id)}
                >
                  <span className="thc-sidebar-icon">{sec.icon}</span>
                  <span>{sec.title}</span>
                </button>
              ))}
            </nav>
            <div className="thc-sidebar-card">
              <p>Need help understanding our policies?</p>
              <a href="mailto:talent@talenthive.com" className="thc-sidebar-email">
                talent@talenthive.com
              </a>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="thc-main">
          <div className="thc-intro-card" data-aos="fade-up">
            <span className="thc-intro-icon">◆</span>
            <p>
              Welcome to TalentHive — India's premier talent platform. These Terms of Service and Privacy Policy govern 
              your use of our website, mobile application, and services. Please read them carefully before proceeding.
            </p>
          </div>

          {sections.map((sec, idx) => (
            <section
              key={sec.id}
              id={sec.id}
              ref={(el) => (sectionRefs.current[sec.id] = el)}
              className="thc-section"
              data-aos="fade-up"
            >
              <div className="thc-section-header">
                <span className="thc-section-num">0{idx + 1}</span>
                <div className="thc-section-title-wrap">
                  <span className="thc-section-icon">{sec.icon}</span>
                  <h2 className="thc-section-title">{sec.title}</h2>
                </div>
              </div>
              <div className="thc-section-body">
                {sec.content.map((block, i) => (
                  <div key={i} className="thc-block">
                    {block.subtitle && <h3 className="thc-block-subtitle">{block.subtitle}</h3>}
                    <p className="thc-block-text" style={{ whiteSpace: "pre-line" }}>{block.text}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}

          {/* Agreement banner */}
          <div className="thc-agreement" data-aos="fade-up">
            <div className="thc-agreement-inner">
              <h3>By using TalentHive, you agree to these terms.</h3>
              <p>Questions or concerns? Our legal team is always here to help.</p>
              <div className="thc-agreement-actions">
                <a href="mailto:talent@talenthive.com" className="thc-btn thc-btn--primary">
                  Contact US
                </a>
                <a href="/" className="thc-btn thc-btn--ghost">Back to Home</a>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Footer - Add your footer component here */}
      {/* <footer className="thc-footer">...</footer> */}
    </div>
  );
}
